import * as React from 'react';
import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isEmpty } from 'lodash/fp';

import {
    TYPE_ARROW_CHART,
    TYPE_BAR_CHART,
    TYPE_DATA_TABLE_CHART,
    TYPE_LINE_CHART,
    TYPE_MAP_CHART,
    TYPE_SCATTER_MAP_CHART,
    TYPE_TREEMAP_CHART,
} from '@portal/common/models/chart-type';
import { DataExportFormat } from '@portal/common/types';

import ChartTypeSelector from '../../../components/ChartTypeSelector';
import _ from '../../../locale';
import { changeSelectedChartType } from '../../../store/data-explorer/actions';
import {
    getDataResponsesByType,
    getDataToolConfig,
    getDataToolSupportedCharts,
    getQueriedConditions,
    getSelectedChartType,
} from '../../../store/data-explorer/selectors';
import ChartSectionRenderer from './ChartSectionRenderer';

const ICONS_BY_CHART_TYPE = {
    [TYPE_LINE_CHART]: '/icons/ic-timeline.svg',
    [TYPE_MAP_CHART]: '/icons/ic-map.svg',
    [TYPE_SCATTER_MAP_CHART]: '/icons/ic-map.svg',
    [TYPE_BAR_CHART]: '/icons/ic-barchart.svg',
    [TYPE_TREEMAP_CHART]: '/icons/ic-treemap.svg',
    [TYPE_ARROW_CHART]: '/icons/ic-arrowchart.svg',
};

type Props = {
    onExportData: (format: DataExportFormat) => void;
    onDataRendered?: () => void;
    areChartsVisible?: boolean;
    theme?: any;
};

const mapValueToLabel = (value) => _(`chart_type_label_${value}`);

const ChartSection: FC<Props> = (props: Props) => {
    const { onExportData, onDataRendered, theme, areChartsVisible = true } = props;

    const dispatch = useDispatch();

    const conditions = useSelector(getQueriedConditions);
    const chartType = useSelector(getSelectedChartType);
    const dataToolConfig = useSelector(getDataToolConfig);
    const dataResponsesByType = useSelector(getDataResponsesByType);
    const supportedCharts = useSelector(getDataToolSupportedCharts);

    const handleChartTypeChange = (value: any) => {
        dispatch(changeSelectedChartType(value));
    };

    if (
        !(!isEmpty(conditions) && dataToolConfig && dataResponsesByType) ||
        isEmpty(supportedCharts)
    ) {
        return null;
    }

    const config = supportedCharts.reduce((charts, type) => {
        if (areChartsVisible || type === TYPE_DATA_TABLE_CHART) {
            charts.push({
                value: type,
                icon: ICONS_BY_CHART_TYPE[type],
            });
        }

        return charts;
    }, []);

    return (
        <div key={supportedCharts.join(',')}>
            <ChartTypeSelector
                config={config}
                value={chartType}
                mapValueToLabel={mapValueToLabel}
                onSelect={handleChartTypeChange}
            />
            <ChartSectionRenderer
                onExportData={onExportData}
                onDataRendered={onDataRendered}
                theme={theme}
            />
        </div>
    );
};

export default ChartSection;
